import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Building2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { isMaintenanceActive } from "@shared/maintenance";
import { ComingSoonBadge } from "@/components/coming-soon-panel";
import { fileUrl } from "@/lib/format";
import { useDocumentTitle } from "@/lib/use-document-title";
import { ScrollReveal } from "@/components/scroll-reveal";
import type { Project } from "@shared/schema";

export function ProjectsPage() {
  useDocumentTitle("Projektek");
  const q = useQuery({
    queryKey: ["projects"],
    queryFn: () => api<Project[]>("/api/projects", { withAuth: false }),
  });

  return (
    <div>
      {/* Hero band with watermark */}
      <section className="relative bg-white py-24 lg:py-32 section-titled overflow-hidden">
        <div className="section-watermark uppercase">projekt</div>
        <div className="container relative z-10 max-w-3xl text-center">
          <ScrollReveal immediate>
            <span className="section-eyebrow">Portfólió</span>
            <h1 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-heading font-normal uppercase tracking-ultrawide leading-tight">
              <span className="text-brand-700">Projektjeink</span>{" "}
              <span className="text-accent">összefoglalva</span>
            </h1>
            <p className="mt-6 text-brand-500 leading-relaxed">
              Aktuális és korábbi megvalósításaink — tekintse meg a
              részleteket és kérjen ajánlatot.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-brand-50">
        <div className="container">
          {q.isLoading && (
            <div className="text-brand-500 text-center py-20">
              Betöltés...
            </div>
          )}

          {q.data?.length === 0 && (
            <div className="text-brand-500 text-center py-20">
              Jelenleg nincs publikált projekt.
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4 lg:gap-6 max-w-7xl mx-auto">
            {q.data?.map((p, i) => (
              <ScrollReveal key={p.id} delay={(i % 3) as 0 | 1 | 2}>
                <ProjectCard project={p} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = {
  planning: "Tervezés alatt",
  construction: "Kivitelezés alatt",
  sales: "Értékesítés alatt",
  completed: "Befejezett",
};

function ProjectCard({ project }: { project: Project }) {
  // Projekt-szintű „Hamarosan": a kártya marad (név + borító), de jelezzük,
  // hogy a részletek még nem nyilvánosak.
  const comingSoon = isMaintenanceActive(project.maintenance);
  return (
    <Link
      href={`/projektek/${project.slug}`}
      className="group relative block overflow-hidden bg-brand-200 aspect-[4/3]"
    >
      {project.coverImageKey ? (
        <img
          src={fileUrl(project.coverImageKey)}
          alt={project.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      ) : (
        <div className="w-full h-full grid place-items-center bg-gradient-to-br from-brand-300 via-brand-400 to-brand-600 text-white/30">
          <Building2 size={96} strokeWidth={0.8} />
        </div>
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />

      <div className="absolute inset-x-0 top-0 p-6 lg:p-8">
        <h3 className="text-white font-heading uppercase tracking-widish text-lg lg:text-xl text-shadow-strong">
          {project.name}
        </h3>
        {project.locationAddress && (
          <p className="mt-1 text-white/70 text-xs uppercase tracking-widish">
            {project.locationAddress}
          </p>
        )}
      </div>

      <div
        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 text-[10px] font-semibold uppercase tracking-widish"
        style={{ writingMode: "vertical-rl", transform: "rotate(180deg) translateX(50%)" }}
      >
        {STATUS_LABEL[project.status]}
      </div>

      {comingSoon && (
        <ComingSoonBadge className="absolute left-6 lg:left-8 bottom-6 lg:bottom-8" />
      )}

      <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8 text-white">
        {project.shortDescription && (
          <p className="text-sm text-white/90 line-clamp-2 mb-2 max-w-md">
            {project.shortDescription}
          </p>
        )}
        <div className="flex items-center gap-2 text-xs uppercase tracking-widish font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
          <span>Megnézem</span>
          <ArrowRight size={14} />
        </div>
      </div>
    </Link>
  );
}
